import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { useSearchParams } from 'react-router-dom';

import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

const FlaggedMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('messageId');
  const highlightRef = useRef(null);

  useEffect(() => {
    loadFlaggedMessages();
  }, []);

  const loadFlaggedMessages = async () => {
    try {
      setLoading(true);

      // contract_id FK → marketplace_contracts (joinable); sender/receiver FK → auth.users (not joinable)
      const { data: msgData, error } = await supabase
        ?.from('messages')
        ?.select(`
          *,
          contract:contract_id(contract_number, title)
        `)
        ?.eq('flagged_offplatform', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Resolve sender/receiver emails from user_profiles
      const userIds = [...new Set([
        ...(msgData || [])?.map(m => m?.sender_id),
        ...(msgData || [])?.map(m => m?.receiver_id),
      ]?.filter(Boolean))];

      let userMap = {};
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, email')
          ?.in('id', userIds);

        userMap = (profiles || [])?.reduce((acc, p) => {
          acc[p?.id] = p;
          return acc;
        }, {});
      }

      const enriched = (msgData || [])?.map(m => ({
        ...m,
        sender: userMap[m?.sender_id] || null,
        receiver: userMap[m?.receiver_id] || null,
      }));

      setMessages(enriched);
    } catch (error) {
      console.error('Load flagged messages error:', error);
      toast?.error('Failed to load flagged messages');
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to the highlighted message after data loads
  useEffect(() => {
    if (!loading && highlightId && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [loading, highlightId]);

  const severityColors = {
    low: 'bg-info/10 text-info',
    medium: 'bg-warning/10 text-warning',
    high: 'bg-error/10 text-error',
    critical: 'bg-error text-white'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Flagged Messages</h3>
        <div className="text-sm text-muted-foreground">
          Total Flagged: {messages?.length}
        </div>
      </div>

      {messages?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" className="w-16 h-16 text-success mx-auto mb-4" />
          <p className="text-muted-foreground">No flagged messages</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages?.map((message) => {
            const isHighlighted = highlightId && message?.id === highlightId;
            return (
            <div
              key={message?.id}
              ref={isHighlighted ? highlightRef : null}
              className={`bg-card border rounded-lg p-4 hover:shadow-elevation-md transition-smooth ${
                isHighlighted
                  ? 'border-warning ring-2 ring-warning/40'
                  : 'border-border'
              }`}
            >
              <div className="flex items-start gap-3 mb-3">
                <Icon name="AlertTriangle" className="w-5 h-5 text-warning flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-foreground">
                      {message?.contract?.title || 'Unknown Contract'}
                    </h4>
                    <span className="px-2 py-1 text-xs font-medium rounded bg-error/10 text-error">
                      Off-Platform Contact
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <span className="text-muted-foreground">Contract #:</span>
                      <span className="text-foreground ml-2">
                        {message?.contract?.contract_number || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Sender:</span>
                      <span className="text-foreground ml-2">{message?.sender?.email}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Detected Patterns:</span>
                      <span className="text-foreground ml-2">
                        {message?.detected_patterns?.join(', ') || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Date:</span>
                      <span className="text-foreground ml-2">
                        {new Date(message?.created_at)?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="bg-muted/50 p-3 rounded border border-border">
                    <p className="text-sm text-foreground whitespace-pre-wrap">
                      {message?.message_text}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FlaggedMessages;